import { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteEntity,
  disposePage,
  entityDeleted,
  getConnectionsList,
  getGoogleAuth,
  loadDataset,
  loadDatasets,
  loadEntity,
  loadGoogleSheetId,
  loadGoogleSheets,
  loadPage,
  pauseSchedule,
  removeEntityFromUI,
  resumeSchedule,
  saveEntity,
  setFormServerErrors,
  setFormValue,
  setPageValue,
  setPanes,
  setProcessing,
  showHideGoogleSheets,
} from "../../../redux/slices/scheduleviewpageslice";
import { addNotification } from "../../../redux/slices/notificationsslice";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import {
  deepCompare,
  isNullOrEmpty,
  notificationDurationMiddle,
  updateRuleAtPath,
} from "../../../lib/utils";
import { Page } from "../../page";
import {
  setCurrentPage,
  setCurrentPageIsDirty,
} from "../../../redux/slices/currentpageslice";
import { BLOCK_DIRTY_MESSAGE } from "../../../settings";
import { ScheduleViewPageBody } from "./scheduleviewpagebody";
import { useEffectOnce } from "../../../hooks/useeffectonce";
import {
  hideAppDialog,
  setAppDialog,
} from "../../../redux/slices/appdialogslice";

export const ScheduleViewPage = (props) => {
  const dispatch = useDispatch();
  const pageState = useSelector((state) => state.scheduleViewPage);
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const currentPageState = useSelector((state) => state.currentPage);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");

  const { t } = useTranslation();

  let currentPageId = "scheduleViewPage";

  /*initial page load*/
  useEffect(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.create.loading_errormessage"),
                type: "error",
                closable: true,
                duration: 30000,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: 3,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.create.unknown_error"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, [location.pathname]);
  /*initial load*/

  useEffectOnce(() => {
    let _id = id;
    if (!isNullOrEmpty(_id)) {
      _id = _id.toString().trim();
      handleOnEntitySelected(_id, type);
    }
    return () => {
      document.title = t("datasets.datasetview.intelligence");
      dispatch(disposePage(true));
    };
  }, [id]);

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let isDirty = !deepCompare(
      pageState.formInitialValues,
      pageState.formValues,
    );
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*toolbar*/
  const handleOnClose = () => {
    if (blockNavigationIfDirty() == true) return;
    navigate("/schedule");
  };

  const handleOnNew = (e) => {
    if (blockNavigationIfDirty() == true) return;
    navigate("/createschedule");
  };

  const handleOnSave = (e) => {
    //no client errors => proceed
    dispatch(saveEntity(type, t)).then(
      (response) => {
        let successMessage = t("schedule.updatedSchedule");

        dispatch(
          addNotification({
            text: successMessage,
            type: "success",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          }),
        );
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.create.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
            if (payload.data.ErrorCodes.EntityDoesNotExist) {
              dispatch(removeEntityFromUI(pageState.formValues.Id));
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: 3,
                  position: "topRight",
                }),
              );
            } else dispatch(setFormServerErrors(errorMessages));
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.create.unknown_error");
          dispatch(setFormServerErrors([errorMessage]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleOnDelete = (referenceKey, type) => {
    dispatch(
      setAppDialog({
        isVisible: true,
        className: "datasetslocator-page-are-you-sure",
        title: t("datasets.datasetview.warning"),
        content: t("schedule.deletequestion"),
        actions: {
          yesButton: {
            title: t("datasets.datasetview.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(referenceKey, type, t)).then(
                (response) => {
                  dispatch(
                    addNotification({
                      text: t("schedule.deletesuccess"),
                      type: "success",
                      closable: true,
                      duration: 3,
                      position: "topRight",
                    }),
                  );
                  navigate(`/schedule`, { replace: true });
                },
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("datasets.create.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(entityDeleted(referenceKey));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessage = t("datasets.create.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                },
              );
            },
          },
          noButton: { title: t("datasets.datasetview.no") },
        },
      }),
    );
  };

  const handlePauseSchedule = (id, type) => {
    dispatch(pauseSchedule(id, type, t)).then(
      (response) => {
        dispatch(setFormValue({ id: "IsActive", value: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.locator.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.locator.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleResumeSchedule = (id, type) => {
    dispatch(resumeSchedule(id, type, t)).then(
      (response) => {
        dispatch(setFormValue({ id: "IsActive", value: true }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.locator.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.locator.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };
  /*toolbar*/

  /*view/edit entity*/
  const handleOnEntitySelected = (entityId, type) => {
    //if (blockNavigationIfDirty()) return;

    if (Number(id) !== entityId) {
      navigate(`/scheduleview/${entityId}?type=${type}`, {
        replace: true,
      }); // Adjust path if needed
    }

    dispatch(loadEntity(entityId, type, t)).then(
      (response) => {
        dispatch(
          setFormValue({
            id: "Id",
            value: entityId,
          }),
        );
        if (response?.data?.Data?.DatasetId) {
          dispatch(loadDataset(response?.data?.Data?.DatasetId, t)).then(
            (res) => {},
            (err) => {
              //hide loader
              dispatch(setProcessing({ processing: false }));

              const handleResponseReceived = (
                state,
                dispatch,
                navigate,
                payload,
              ) => {
                let errorMessages = payload?.data?.ErrorMessages;

                if (!errorMessages || errorMessages.length == 0)
                  errorMessages = [t("datasets.create.unknown_error")];
                else {
                  errorMessages = getErrorMessages(errorMessages, 1);

                  if (payload.data.ErrorCodes.EntityDoesNotExist)
                    dispatch(removeEntityFromUI(entityId));
                }

                dispatch(
                  addNotification({
                    text: errorMessages[0],
                    type: "error",
                    closable: true,
                    duration: 3,
                    position: "topRight",
                  }),
                );
              };

              const handleUnhandled = (state, dispatch, navigate, payload) => {
                let errorMessage = t("datasets.create.unknown_error");
                dispatch(
                  addNotification({
                    text: errorMessage,
                    type: "error",
                    closable: true,
                    duration: 3,
                    position: "topRight",
                  }),
                );
              };

              apiErrorsHandler({}, dispatch, navigate, err, null, {
                responseReceived: handleResponseReceived,
                unhandled: handleUnhandled,
              });
            },
          );
        }
      },
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datasets.create.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist)
              dispatch(removeEntityFromUI(entityId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datasets.create.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleRecurrencyOptionChange = (e) => {
    const value = e.value;
    dispatch(setFormValue({ id: "RecurrencyFrom", value }));
  };
  /*view/edit entity*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/
  //tools visible/hidden
  const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "100%",
            collapsible: false,
          },
        ]),
      );
    else
      dispatch(
        setPanes([
          {
            size: "20%",
            min: "20%",
            max: "25%",
            collapsible: false,
          },
          {
            min: "70%",
            max: "80%",
            collapsible: false,
          },
        ]),
      );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
  };

  const loadGoogleConnectorSheets = (t) => {
    dispatch(loadGoogleSheets(pageState.formValues.ConnectorConfigId, t)).then(
      (response) => {
        dispatch(showHideGoogleSheets(true));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        if (err.data.ErrorCodes.GoogleUnAuthorized) {
          dispatch(getGoogleAuth(t)).then(
            (response) => {},
            (err) => {
              //hide loader
              dispatch(setProcessing({ processing: false }));

              const handleResponseReceived = (
                state,
                dispatch,
                navigate,
                payload,
              ) => {
                let errorMessages = payload?.data?.ErrorMessages;

                //notify with error
                if (!errorMessages || errorMessages.length == 0)
                  dispatch(
                    addNotification({
                      text: t("datasets.create.loading_error"),
                      type: "error",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    }),
                  );
                else {
                  errorMessages = getErrorMessages(errorMessages, 1);
                  dispatch(
                    addNotification({
                      text: errorMessages[0],
                      type: "error",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    }),
                  );
                }

                //go home
                //navigate("/", { replace: true });
              };

              const handleUnhandled = (state, dispatch, navigate, payload) => {
                dispatch(
                  addNotification({
                    text: t("datasets.create.unknown_error"),
                    type: "error",
                    closable: true,
                    duration: notificationDurationMiddle,
                    position: "topRight",
                  }),
                );
                //navigate("/", { replace: true });
              };

              //console.log(action);
              apiErrorsHandler({}, dispatch, navigate, err, "/", {
                responseReceived: handleResponseReceived,
                unhandled: handleUnhandled,
              });
            },
          );
          //navigate("/", { replace: true });
        } else {
          const handleResponseReceived = (
            state,
            dispatch,
            navigate,
            payload,
          ) => {
            let errorMessages = payload?.data?.ErrorMessages;

            //notify with error
            if (!errorMessages || errorMessages.length == 0)
              dispatch(
                addNotification({
                  text: t("schedule.googlesheeterror"),
                  type: "error",
                  closable: true,
                  duration: notificationDurationMiddle,
                  position: "topRight",
                }),
              );
            else {
              errorMessages = getErrorMessages(errorMessages, 1);
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: notificationDurationMiddle,
                  position: "topRight",
                }),
              );
            }

            //go home
            //navigate("/", { replace: true });
          };

          const handleUnhandled = (state, dispatch, navigate, payload) => {
            dispatch(
              addNotification({
                text: t("datasets.create.unknown_error"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
            //navigate("/", { replace: true });
          };

          //console.log(action);
          apiErrorsHandler({}, dispatch, navigate, err, "/", {
            responseReceived: handleResponseReceived,
            unhandled: handleUnhandled,
          });
        }
      },
    );
  };

  const loadGoogleConnections = (t) => {
    dispatch(getConnectionsList(currentUser.AppUserId, t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        if (err.data.ErrorCodes.GoogleUnAuthorized) {
          dispatch(getGoogleAuth(t)).then(
            (response) => {},
            (err) => {
              //hide loader
              dispatch(setProcessing({ processing: false }));

              const handleResponseReceived = (
                state,
                dispatch,
                navigate,
                payload,
              ) => {
                let errorMessages = payload?.data?.ErrorMessages;

                //notify with error
                if (!errorMessages || errorMessages.length == 0)
                  dispatch(
                    addNotification({
                      text: t("datasets.create.loading_error"),
                      type: "error",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    }),
                  );
                else {
                  errorMessages = getErrorMessages(errorMessages, 1);
                  dispatch(
                    addNotification({
                      text: errorMessages[0],
                      type: "error",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    }),
                  );
                }

                //go home
                //navigate("/", { replace: true });
              };

              const handleUnhandled = (state, dispatch, navigate, payload) => {
                dispatch(
                  addNotification({
                    text: t("datasets.create.unknown_error"),
                    type: "error",
                    closable: true,
                    duration: notificationDurationMiddle,
                    position: "topRight",
                  }),
                );
                //navigate("/", { replace: true });
              };

              //console.log(action);
              apiErrorsHandler({}, dispatch, navigate, err, "/", {
                responseReceived: handleResponseReceived,
                unhandled: handleUnhandled,
              });
            },
          );
          //navigate("/", { replace: true });
        } else {
          const handleResponseReceived = (
            state,
            dispatch,
            navigate,
            payload,
          ) => {
            let errorMessages = payload?.data?.ErrorMessages;

            //notify with error
            if (!errorMessages || errorMessages.length == 0)
              dispatch(
                addNotification({
                  text: t("schedule.connectionserror"),
                  type: "error",
                  closable: true,
                  duration: notificationDurationMiddle,
                  position: "topRight",
                }),
              );
            else {
              errorMessages = getErrorMessages(errorMessages, 1);
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: notificationDurationMiddle,
                  position: "topRight",
                }),
              );
            }

            //go home
            //navigate("/", { replace: true });
          };

          const handleUnhandled = (state, dispatch, navigate, payload) => {
            dispatch(
              addNotification({
                text: t("datasets.create.unknown_error"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
            //navigate("/", { replace: true });
          };

          //console.log(action);
          apiErrorsHandler({}, dispatch, navigate, err, "/", {
            responseReceived: handleResponseReceived,
            unhandled: handleUnhandled,
          });
        }
      },
    );
  };

  const getGoogleSheetId = (sheetId, sheetName, sheetPath, connectionId, t) => {
    dispatch(
      loadGoogleSheetId(sheetId, sheetName, sheetPath, connectionId, t),
    ).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("schedule.googlesheetid"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          //navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.create.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          //navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleOnChangeFormValue = (name, id) => {
    dispatch(setFormValue({ id: name, value: id }));
  };

  const handleKeyChange = (keyId, path) => {
    const currentQuery = pageState.formValues.WhereObject;
    const updatedQuery = updateRuleAtPath(currentQuery, path, keyId);
    dispatch(setFormValue({ id: "WhereObject", value: updatedQuery }));
  };

  const handleLoadDatasets = (t) => {
    dispatch(loadDatasets(t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.create.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          //navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.create.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          //navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  return (
    <Fragment>
      <Page
        id="scheduleViewPage"
        className="scheduleView-page"
        title={t("schedule.scheduleview")}
        animation={props.animation}
      >
        <ScheduleViewPageBody
          onSplitterChange={handleOnSplitterChange}
          onClose={handleOnClose}
          onChangeFormValue={handleOnChangeFormValue}
          onToolsToggle={handleOnToolsToggle}
          onSave={handleOnSave}
          onDelete={handleOnDelete}
          onNew={handleOnNew}
          onEntitySelected={handleOnEntitySelected}
          onEntityDoubleClick={handleOnEntitySelected}
          updateDirtyState={updateDirtyState}
          onRecurrencyOptionChange={handleRecurrencyOptionChange}
          loadGoogleConnectorSheets={loadGoogleConnectorSheets}
          loadGoogleConnections={loadGoogleConnections}
          getGoogleSheetId={getGoogleSheetId}
          onKeyChange={handleKeyChange}
          onPauseSchedule={handlePauseSchedule}
          onResumeSchedule={handleResumeSchedule}
          loadDatasets={handleLoadDatasets}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .scheduleView-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .scheduleView-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .scheduleView-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .scheduleView-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .scheduleView-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    .scheduleView-page > .content .scheduleView-page-manager{   
                        height:100%;                     
                        background-color: rgba(0, 0, 0, 0.02);
                    }
                    `}
      </style>
    </Fragment>
  );
};
